import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/lib/api';
import type { Notification, PaginatedResponse } from '@/types';

/** Backend returns a plain array; UI/cache expect PaginatedResponse. */
function normalizeNotificationsResponse(
  raw: unknown,
  limit: number
): PaginatedResponse<Notification> {
  if (Array.isArray(raw)) {
    const data = raw as Notification[];
    return {
      data,
      meta: { total: data.length, limit },
    };
  }
  const page = raw as PaginatedResponse<Notification> | null | undefined;
  if (page && Array.isArray(page.data)) {
    return page;
  }
  return { data: [], meta: { total: 0, limit } };
}

export function useNotifications(limit = 50) {
  return useQuery({
    queryKey: ['notifications', { limit }],
    queryFn: async () => {
      const response = await notificationsApi.getAll({ limit });
      return normalizeNotificationsResponse(response.data, limit);
    },
    staleTime: 15_000,
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await notificationsApi.getUnreadCount();
      return response.data.count as number;
    },
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await notificationsApi.markAsRead(id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      const previousNotifications = queryClient.getQueryData<PaginatedResponse<Notification>>([
        'notifications',
        { limit: 50 },
      ]);
      const previousCount = queryClient.getQueryData<number>(['notifications', 'unread-count']);

      if (previousNotifications) {
        queryClient.setQueryData<PaginatedResponse<Notification>>(
          ['notifications', { limit: 50 }],
          {
            ...previousNotifications,
            data: previousNotifications.data.map((n) =>
              n.id === id ? { ...n, isRead: true } : n
            ),
          }
        );
      }

      if (typeof previousCount === 'number' && previousCount > 0) {
        queryClient.setQueryData(['notifications', 'unread-count'], previousCount - 1);
      }

      return { previousNotifications, previousCount };
    },
    onError: (_, __, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications', { limit: 50 }], context.previousNotifications);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(['notifications', 'unread-count'], context.previousCount);
      }
    },
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await notificationsApi.markAllAsRead();
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      const previousNotifications = queryClient.getQueryData<PaginatedResponse<Notification>>([
        'notifications',
        { limit: 50 },
      ]);
      const previousCount = queryClient.getQueryData<number>(['notifications', 'unread-count']);

      if (previousNotifications) {
        queryClient.setQueryData<PaginatedResponse<Notification>>(
          ['notifications', { limit: 50 }],
          {
            ...previousNotifications,
            data: previousNotifications.data.map((n) => ({ ...n, isRead: true })),
          }
        );
      }

      queryClient.setQueryData(['notifications', 'unread-count'], 0);

      return { previousNotifications, previousCount };
    },
    onError: (_, __, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications', { limit: 50 }], context.previousNotifications);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(['notifications', 'unread-count'], context.previousCount);
      }
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await notificationsApi.delete(id);
    },
    onSuccess: (_, id) => {
      const notifications = queryClient.getQueryData<PaginatedResponse<Notification>>([
        'notifications',
        { limit: 50 },
      ]);
      const wasUnread = notifications?.data.find((n) => n.id === id && !n.isRead);

      queryClient.setQueryData<PaginatedResponse<Notification>>(
        ['notifications', { limit: 50 }],
        (old) => {
          if (!old) return old;
          const newData = old.data.filter((n) => n.id !== id);
          return {
            ...old,
            data: newData,
            meta: { ...old.meta, total: old.meta.total - 1 },
          };
        }
      );

      const previousCount = queryClient.getQueryData<number>(['notifications', 'unread-count']);
      if (typeof previousCount === 'number' && wasUnread) {
        queryClient.setQueryData(['notifications', 'unread-count'], Math.max(0, previousCount - 1));
      }
    },
  });
}
